/**
 * Componente para exibir mensagens de documento no chat
 * 
 * Exibe ícone dinâmico por tipo de arquivo, nome, tamanho
 * e botões de ação (download/abrir)
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - Passa a exibir a legenda (content) abaixo do
 *            cartão do arquivo, ignorando o fallback "📄 nome-do-arquivo".
 */

import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { 
  FileText, 
  FileSpreadsheet, 
  Presentation, 
  FileArchive, 
  File,
  Download,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DocumentMessageBubbleProps {
  /** URL do documento (pode ser relativa do storage ou absoluta) */
  url: string;
  /** Nome do arquivo */
  filename?: string | null;
  /** Tipo MIME do arquivo */
  mimeType?: string | null;
  /** Tamanho em bytes */
  sizeBytes?: number | null;
  /** Direção da mensagem (entrada/saída) */
  direction: 'inbound' | 'outbound';
  /** Legenda/texto que acompanha o documento (opcional) */
  caption?: string | null;
}

/**
 * Retorna o ícone apropriado baseado no tipo MIME do arquivo
 */
function getDocumentIcon(mimeType: string | null | undefined) {
  if (!mimeType) return <File className="h-5 w-5" />;
  
  // PDF
  if (mimeType.includes('pdf')) {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  
  // Word
  if (mimeType.includes('word') || mimeType.includes('document') || mimeType.includes('msword')) {
    return <FileText className="h-5 w-5 text-blue-500" />;
  }
  
  // Excel
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('csv')) {
    return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  }
  
  // PowerPoint
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
    return <Presentation className="h-5 w-5 text-orange-500" />;
  }
  
  // Arquivos compactados
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z') || mimeType.includes('archive')) {
    return <FileArchive className="h-5 w-5 text-yellow-500" />;
  }
  
  // Texto simples
  if (mimeType.includes('text/plain')) {
    return <FileText className="h-5 w-5 text-muted-foreground" />;
  }
  
  // Default
  return <File className="h-5 w-5 text-muted-foreground" />;
}

/**
 * Formata o tamanho do arquivo para exibição
 */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

/**
 * Retorna a extensão correta baseada no MIME type
 * Usado para corrigir arquivos que foram salvos com extensão .bin
 */
function getCorrectExtension(mimeType: string | null | undefined): string | null {
  if (!mimeType) return null;
  
  const mimeToExtension: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'text/csv': 'csv',
    'text/plain': 'txt',
    'application/zip': 'zip',
    'application/x-zip-compressed': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/x-7z-compressed': '7z',
    'application/vnd.oasis.opendocument.text': 'odt',
    'application/vnd.oasis.opendocument.spreadsheet': 'ods',
    'application/vnd.oasis.opendocument.presentation': 'odp',
  };
  
  return mimeToExtension[mimeType] || null;
}

/**
 * Extrai a extensão do arquivo do nome ou mime type
 */
function getFileExtension(filename: string | null | undefined, mimeType: string | null | undefined): string {
  if (filename) {
    const parts = filename.split('.');
    if (parts.length > 1) {
      return parts[parts.length - 1].toUpperCase();
    }
  }
  
  if (mimeType) {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'XLS';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PPT';
    if (mimeType.includes('zip')) return 'ZIP';
    if (mimeType.includes('rar')) return 'RAR';
    if (mimeType.includes('text/plain')) return 'TXT';
    if (mimeType.includes('csv')) return 'CSV';
  }
  
  return 'DOC';
}

export function DocumentMessageBubble({
  url,
  filename,
  mimeType,
  sizeBytes,
  direction,
  caption,
}: DocumentMessageBubbleProps) {
  // Resolve URL completa do storage
  const fileUrl = getStorageUrl(url) || url;
  
  // Nome do arquivo para exibição (truncado se muito longo)
  const displayName = filename || 'Documento';
  const truncatedName = displayName.length > 18 
    ? displayName.slice(0, 15) + '...' 
    : displayName;
  
  // Extensão do arquivo
  const extension = getFileExtension(filename, mimeType);
  
  // Tamanho formatado
  const formattedSize = formatFileSize(sizeBytes);
  
  // Cores baseadas na direção
  const isOutbound = direction === 'outbound';
  
  // Handler para download - corrige extensão se nome termina em .bin mas MIME type indica outro formato
  const handleDownload = () => {
    let downloadName = displayName;
    
    // Corrigir extensão para arquivos salvos com .bin (retrocompatibilidade)
    if (downloadName.endsWith('.bin') && mimeType) {
      const correctExtension = getCorrectExtension(mimeType);
      if (correctExtension && correctExtension !== 'bin') {
        downloadName = downloadName.replace(/\.bin$/, `.${correctExtension}`);
      }
    }
    
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = downloadName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Handler para abrir em nova aba
  const handleOpen = () => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  // Legenda válida: ignora o fallback histórico "📄 nome-do-arquivo"
  const trimmedCaption = (caption || '').trim();
  const showCaption =
    !!trimmedCaption &&
    trimmedCaption !== `📄 ${displayName}` &&
    trimmedCaption !== displayName;

  const card = (
    <div className={cn(
      "flex items-center gap-2 p-2 rounded-lg min-w-[140px] max-w-[180px]",
      !isOutbound && "bg-background/50 border border-border/50"
    )}
    style={isOutbound ? { 
      backgroundColor: 'hsl(var(--chat-bubble-inbound) / 0.15)',
      border: '1px solid hsl(var(--chat-border) / 0.5)'
    } : undefined}
    >
      {/* Ícone do documento */}
      <div className={cn(
        "flex-shrink-0 p-1.5 rounded",
        !isOutbound && "bg-muted"
      )}
      style={isOutbound ? { backgroundColor: 'hsl(var(--chat-text-primary) / 0.08)' } : undefined}
      >
        {getDocumentIcon(mimeType)}
      </div>
      
      {/* Informações do arquivo */}
      <div className="flex-1 min-w-0">
        <p 
          className={cn("text-xs font-medium truncate", !isOutbound && "text-foreground")}
          style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
          title={displayName}
        >
          {truncatedName}
        </p>
        <div 
          className={cn("flex items-center gap-1 text-[10px]", !isOutbound && "text-muted-foreground")}
          style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
        >
          <span className="font-medium">{extension}</span>
          {formattedSize && (
            <>
              <span>•</span>
              <span>{formattedSize}</span>
            </>
          )}
        </div>
      </div>
      
      {/* Botões de ação - cores fixas WhatsApp para outbound */}
      <div className="flex-shrink-0 flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6",
            !isOutbound && "text-muted-foreground hover:text-foreground"
          )}
          style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
          onClick={handleDownload}
          title="Baixar arquivo"
        >
          <Download className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6",
            !isOutbound && "text-muted-foreground hover:text-foreground"
          )}
          style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
          onClick={handleOpen}
          title="Abrir em nova aba"
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );

  // Sem legenda: mantém exatamente o layout original (cartão isolado)
  if (!showCaption) return card;

  // Com legenda: cartão + texto abaixo (padrão WhatsApp)
  return (
    <div className="flex flex-col gap-1">
      {card}
      <p
        className={cn(
          "text-sm whitespace-pre-wrap break-words max-w-[280px]",
          !isOutbound && "text-foreground"
        )}
        style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
      >
        {trimmedCaption}
      </p>
    </div>
  );
}
